import React, { useEffect, useRef, useState } from 'react';
import P from 'prop-types';
import { useDispatch } from 'react-redux';

import { CenteredContainer } from '@nerdwallet/currency';
import { COMPONENT_UPDATED } from '~/app/lib/redux/actions/action-types';
import { useIdentity } from '~/app/lib/redux/selectors/identity-selector';
import UniversalContainer, {
  universalContainerPropTypes,
} from '../universal-container';
import editingConfig from './horizontal-banner-editing-config';
import BannerHorizontalContent from './horizontal-banner-content';
import { useHorizontalBanner, defaultProps } from './use-horizontal-banner';
import UniversalText from '../universal-text/universal-text';
import UniversalButton from '../universal-button/universal-button';
import UniversalAuthButton from '../universal-auth-button/universal-auth-button';
import { useBuilder } from '../../lib/redux/selectors/builder-selector';
import './horizontal-banner.scss';

const updateComponent = (component) => ({
  type: COMPONENT_UPDATED,
  component,
});

const ContentContainer = ({
  fullWidth,
  children,
  centeredInParentBackgroundProps,
}) => {
  if (fullWidth) {
    return children;
  }
  return (
    <UniversalContainer
      {...centeredInParentBackgroundProps}
      data-testid="centeredInParentBackgroundContainer"
    >
      <CenteredContainer>{children}</CenteredContainer>
    </UniversalContainer>
  );
};

const HorizontalBanner = ({
  container,
  title,
  subtitle,
  actionButton,
  authButton,
  disableDefaultProps,
  includeSignUpButtons,
  includeContentWidthLimitation,
  includeParentWidthLimitation,
  centeredInParentBackground,
  isCopy,
}) => {
  const { flatOutline, selected: selectedComponentId } = useBuilder();
  const currentComponent = flatOutline?.[selectedComponentId];
  const emptyPropsRef = useRef();
  const [isLoadedTemplate, setIsLoadedTemplate] = useState(null);
  const dispatch = useDispatch();
  const { isSignedIn } = useIdentity();

  const { titleProps, subtitleProps, containerProps, actionButtonProps } =
    useHorizontalBanner({
      title,
      subtitle,
      container,
      actionButton,
      disableDefaultProps,
    });

  useEffect(() => {
    if (
      !isLoadedTemplate &&
      !disableDefaultProps &&
      currentComponent &&
      !currentComponent?.props?.gridItems &&
      !isCopy
    ) {
      setIsLoadedTemplate(true);
      emptyPropsRef.current = currentComponent;
      dispatch(
        updateComponent({
          ...currentComponent,
          props: { ...defaultProps },
        })
      );
    }
  }, [
    currentComponent,
    isLoadedTemplate,
    subtitleProps,
    containerProps,
    disableDefaultProps,
    isCopy,
    dispatch,
  ]);

  useEffect(() => {
    if (disableDefaultProps) {
      dispatch(
        updateComponent({
          ...emptyPropsRef.current,
          props: { disableDefaultProps: true },
        })
      );
      setIsLoadedTemplate(null);
    }
  }, [disableDefaultProps, dispatch]);

  return includeSignUpButtons && isSignedIn ? null : (
    <ContentContainer
      fullWidth={!includeParentWidthLimitation}
      centeredInParentBackgroundProps={centeredInParentBackground}
    >
      <UniversalContainer
        {...containerProps}
        data-testid="horizontalBannerContainer"
      >
        <BannerHorizontalContent
          actionButtonProps={actionButtonProps}
          authButton={authButton}
          includeContentWidthLimitation={includeContentWidthLimitation}
          includeSignUpButtons={includeSignUpButtons}
          isSignedIn={isSignedIn}
          subtitleProps={subtitleProps}
          titleProps={titleProps}
        />
      </UniversalContainer>
    </ContentContainer>
  );
};

ContentContainer.propTypes = {
  fullWidth: P.bool,
  children: P.node,
  centeredInParentBackgroundProps: universalContainerPropTypes,
};

HorizontalBanner.displayName = 'Horizontal Banner';

HorizontalBanner.propTypes = {
  centeredInParentBackground: universalContainerPropTypes,
  disableDefaultProps: P.bool,
  selectedComponentId: P.string,
  updateComponentProps: P.func,
  container: universalContainerPropTypes,
  title: UniversalText.propTypes,
  subtitle: UniversalText.propTypes,
  actionButton: UniversalButton.propTypes,
  authButton: UniversalAuthButton.propTypes,
  includeSignUpButtons: P.bool,
  includeContentWidthLimitation: P.bool,
  includeParentWidthLimitation: P.bool,
  isCopy: P.bool,
  centeredInParentBackgroundProps: P.object,
};

HorizontalBanner.defaultProps = {
  includeSignUpButtons: false,
  includeContentWidthLimitation: false,
  includeParentWidthLimitation: false,
  centeredInParentBackgroundProps: {},
  disableDefaultProps: false,
};

HorizontalBanner.getEditingConfig = editingConfig;

export default HorizontalBanner;
